import { useState, useEffect, useCallback } from 'react';

// Demo-Daten fuer Entwicklung (wenn Backend nicht erreichbar oder Token unbekannt)
const DEMO_PORTAL = {
  lead: {
    id: 'demo',
    status: 'qualifiziert',
    phase: 'ausschreibung',
    next_action: 'Angebote vergleichen',
    next_action_date: new Date(Date.now() + 2 * 86400000).toISOString().slice(0, 10),
    created_at: new Date(Date.now() - 3 * 86400000).toISOString(),
    visit_count: 3,
  },
  company: {
    id: 'demo',
    name: 'Mueller GmbH',
    plz: '10115',
    city: 'Berlin',
    street: 'Friedrichstr. 123',
    industry: 'Metallverarbeitung',
    annual_consumption_kwh: 250000,
    current_price_ct_kwh: 12.5,
    current_supplier: 'Stadtwerke Berlin',
  },
  contact: {
    first_name: 'Hans',
    last_name: 'Mueller',
    email: 'h.mueller@mueller-gmbh.de',
    phone: '+49 30 12345678',
    position: 'Geschaeftsfuehrer',
  },
  offers: [
    {
      id: 1,
      offer_number: 'AQ-2026-1001',
      slug: 'demo-angebot',
      status: 'gesendet',
      valid_until: new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10),
      personal_message: 'Nach unserem Gespraech am Montag hier die besten Angebote fuer Sie.',
      annual_consumption_kwh: 250000,
      total_views: 5,
      options: [
        { id: 1, position: 1, label: 'Festpreis 12 Monate', customer_price_ct_kwh: 9.8, annual_cost_eur: 24500, monthly_cost_eur: 2041.67, savings_eur: 6750, savings_percent: 21.6, contract_duration_months: 12, price_type: 'fixed', is_recommended: true },
        { id: 2, position: 2, label: 'Festpreis 24 Monate', customer_price_ct_kwh: 10.2, annual_cost_eur: 25500, monthly_cost_eur: 2125, savings_eur: 5750, savings_percent: 18.4, contract_duration_months: 24, price_type: 'fixed', is_recommended: false },
        { id: 3, position: 3, label: 'Indexiert (Spot)', customer_price_ct_kwh: 9.2, annual_cost_eur: 23000, monthly_cost_eur: 1916.67, savings_eur: 8250, savings_percent: 26.4, contract_duration_months: 12, price_type: 'indexed', is_recommended: false },
      ],
    },
  ],
  timeline: [
    { id: 1, event_type: 'request_received', title: 'Anfrage eingegangen', description: 'Wir haben Ihre Daten erhalten.', icon: 'inbox', created_at: new Date(Date.now() - 3 * 86400000).toISOString() },
    { id: 2, event_type: 'data_verified', title: 'Daten geprueft', description: 'Ihre Angaben wurden von unserem Team geprueft.', icon: 'check', created_at: new Date(Date.now() - 2 * 86400000).toISOString() },
    { id: 3, event_type: 'tender_started', title: 'Ausschreibung gestartet', description: 'Wir haben bei 7 Versorgern Angebote angefragt.', icon: 'send', created_at: new Date(Date.now() - 2 * 86400000).toISOString() },
    { id: 4, event_type: 'offers_received', title: '5 Angebote eingegangen', description: '5 von 7 Versorgern haben Angebote abgegeben.', icon: 'package', created_at: new Date(Date.now() - 1 * 86400000).toISOString() },
    { id: 5, event_type: 'offer_published', title: 'Angebote freigeschaltet', description: 'Die besten 3 Angebote sind fuer Sie verfuegbar.', icon: 'star', created_at: new Date(Date.now() - 12 * 3600000).toISOString() },
  ],
  documents: [
    { id: 1, document_type: 'angebot', file_name: 'Angebot_AQ-2026-1001.pdf', file_size_bytes: 245000, uploaded_by_customer: false, created_at: new Date(Date.now() - 12 * 3600000).toISOString() },
    { id: 2, document_type: 'lastgang_report', file_name: 'Verbrauchsanalyse_Mueller_GmbH.pdf', file_size_bytes: 180000, uploaded_by_customer: false, created_at: new Date(Date.now() - 1 * 86400000).toISOString() },
  ],
  sales: {
    full_name: 'David Amini',
    role: 'Ihr Ansprechpartner',
    email: 'kontakt@aiqcon.com',
    phone: '+49 170 123 456 78',
  },
};

const PHASES = [
  { id: 'anfrage', label: 'Anfrage', description: 'Daten erhalten' },
  { id: 'pruefung', label: 'Pruefung', description: 'Daten werden geprueft' },
  { id: 'ausschreibung', label: 'Ausschreibung', description: 'Versorger angefragt' },
  { id: 'angebote', label: 'Angebote', description: 'Preise eingetroffen' },
  { id: 'vergleich', label: 'Vergleich', description: 'Optionen verfuegbar' },
  { id: 'entscheidung', label: 'Entscheidung', description: 'Auswahl steht aus' },
  { id: 'vertrag', label: 'Vertrag', description: 'Abschluss + Wechsel' },
];

export const usePortal = (token) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!token) {
      setError('Kein Token');
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`/api/public/portal/${token}`);
      if (res.ok) {
        const json = await res.json();
        setData(json);
        setError(null);
      } else if (res.status === 404) {
        // Demo-Daten nur noch bei ausdruecklichem Demo-Token.
        // Frueher sprang hier auch die Entwicklungsumgebung an — ein Kunde mit
        // ungueltigem Link sah dann die (erfundenen) Daten einer fremden Firma.
        if (token === 'demo') {
          setData(DEMO_PORTAL);
          setError(null);
        } else {
          setData(null);
          setError('Portal nicht gefunden');
        }
      } else {
        throw new Error('HTTP ' + res.status);
      }
    } catch (e) {
      // Backend nicht erreichbar: ehrliche Fehlermeldung statt Fremddaten
      setData(null);
      setError('Ihre Daten sind gerade nicht abrufbar. Bitte versuchen Sie es in Kuerze erneut.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const trackEvent = useCallback((event_type, metadata = {}) => {
    fetch(`/api/public/portal/${token}/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event_type, metadata }),
    }).catch(() => {});
  }, [token]);

  const requestCallback = useCallback(async (payload) => {
    try {
      await fetch(`/api/public/portal/${token}/request-callback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      return { ok: true };
    } catch {
      return { ok: false };
    }
  }, [token]);

  const acceptOffer = useCallback(async (offer_id, option_id) => {
    try {
      await fetch(`/api/public/portal/${token}/accept-offer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ offer_id, option_id }),
      });
      await load();
      return { ok: true };
    } catch {
      return { ok: false };
    }
  }, [token, load]);

  return { data, loading, error, refresh: load, trackEvent, requestCallback, acceptOffer, PHASES };
};

export { PHASES };
